import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AIInsightsPanel = () => {
  const [insights, setInsights] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInsight, setSelectedInsight] = useState(null);

  const mockInsights = [
    {
      id: 1,
      type: 'pattern',
      title: 'Bullish Divergence Detected',
      description: `EUA prices showing strong bullish divergence with RSI indicating oversold conditions. Historical analysis suggests 73% probability of price reversal within 5-7 trading days.`,
      confidence: 87,
      impact: 'high',
      timeframe: '5-7 days',
      icon: 'TrendingUp',
      color: 'success',
      details: {
        technicalIndicators: ['RSI: 28 (Oversold)', 'MACD: Bullish crossover', 'Volume: Above average'],
        historicalAccuracy: '73% success rate',
        riskFactors: ['Regulatory announcements', 'Weather patterns', 'Oil price correlation']
      }
    },
    {
      id: 2,
      type: 'volatility',
      title: 'Increased Volatility Expected',
      description: `AI models predict 40% increase in volatility over next 2 weeks due to upcoming EU climate policy announcements and seasonal demand patterns.`,
      confidence: 92,
      impact: 'medium',
      timeframe: '2 weeks',
      icon: 'Zap',
      color: 'warning',
      details: {
        volatilityForecast: 'Current: 15.2% → Expected: 21.3%',
        catalysts: ['EU Climate Policy Review', 'Q4 Compliance Deadline', 'Winter Demand Surge'],
        tradingImplications: ['Wider spreads expected', 'Increased margin requirements', 'Higher option premiums']
      }
    },
    {
      id: 3,
      type: 'correlation',
      title: 'Oil-EUA Correlation Breakdown',
      description: `Traditional correlation between Oil futures and EUA prices has weakened to 0.23 (from 0.73), suggesting independent price movements ahead.`,
      confidence: 78,
      impact: 'medium',
      timeframe: '1 month',
      icon: 'GitBranch',
      color: 'primary',
      details: {
        correlationChange: '0.73 → 0.23 (68% decrease)',
        driverFactors: ['Renewable energy adoption', 'Independent supply dynamics', 'Policy divergence'],
        tradingStrategy: ['Reduce hedging strategies', 'Focus on EUA fundamentals', 'Monitor correlation recovery']
      }
    },
    {
      id: 4,
      type: 'risk',
      title: 'Downside Risk Alert',
      description: `Machine learning models identify 65% probability of 8-12% price correction if support level at €82.50 is breached with high volume.`,
      confidence: 81,
      impact: 'high',
      timeframe: '3-5 days',
      icon: 'AlertTriangle',
      color: 'error',
      details: {
        supportLevels: ['Primary: €82.50', 'Secondary: €79.20', 'Critical: €75.80'],
        volumeThreshold: '2.5M+ contracts',
        riskMitigation: ['Stop-loss at €82.00', 'Hedge with put options', 'Reduce position size']
      }
    },
    {
      id: 5,
      type: 'opportunity',
      title: 'Arbitrage Opportunity',
      description: `Price discrepancy detected between EEX and ICE markets. Spread of €1.20 exceeds historical norm, presenting low-risk arbitrage opportunity.`,
      confidence: 95,
      impact: 'low',
      timeframe: 'Immediate',
      icon: 'Target',
      color: 'accent',
      details: {
        spreadAnalysis: 'Current: €1.20 vs Historical Avg: €0.35',
        executionWindow: '2-4 hours',
        expectedReturn: '0.8-1.2% risk-adjusted return'
      }
    }
  ];

  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      setInsights(mockInsights);
      setIsLoading(false);
    }, 1500);
  }, []);

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-success';
    if (confidence >= 70) return 'text-primary';
    if (confidence >= 50) return 'text-warning';
    return 'text-error';
  };

  const getImpactBadge = (impact) => {
    const colors = {
      high: 'bg-error text-error-foreground',
      medium: 'bg-warning text-warning-foreground',
      low: 'bg-muted text-muted-foreground'
    };
    return colors?.[impact] || colors?.low;
  };

  const getInsightColor = (color) => {
    const colors = {
      success: 'text-success',
      warning: 'text-warning',
      primary: 'text-primary',
      error: 'text-error',
      accent: 'text-accent'
    };
    return colors?.[color] || colors?.primary;
  };

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Brain" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">AI-Powered Insights</h3>
        </div>
        <div className="space-y-4">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Icon name="Brain" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">AI-Powered Insights</h3>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconSize={14}
          >
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Settings"
            iconSize={14}
          >
            Configure
          </Button>
        </div>
      </div>
      <div className="space-y-4">
        {insights?.map(insight => (
          <div
            key={insight?.id}
            className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-subtle ${
              selectedInsight?.id === insight?.id ? 'border-primary bg-primary/5' : 'border-border'
            }`}
            onClick={() => setSelectedInsight(selectedInsight?.id === insight?.id ? null : insight)}
          >
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg bg-muted ${getInsightColor(insight?.color)}`}>
                <Icon name={insight?.icon} size={16} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium text-foreground">{insight?.title}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getImpactBadge(insight?.impact)}`}>
                    {insight?.impact?.toUpperCase()}
                  </span>
                </div>
                
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  {insight?.description}
                </p>
                
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <Icon name="Target" size={12} />
                    <span className={`font-medium ${getConfidenceColor(insight?.confidence)}`}>
                      {insight?.confidence}% confidence
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="Clock" size={12} />
                    <span className="text-muted-foreground">{insight?.timeframe}</span>
                  </div>
                </div>
              </div>
              
              <Icon 
                name={selectedInsight?.id === insight?.id ? "ChevronUp" : "ChevronDown"} 
                size={16} 
                className="text-muted-foreground mt-1"
              />
            </div>

            {/* Expanded Details */}
            {selectedInsight?.id === insight?.id && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(insight?.details)?.map(([key, value]) => (
                    <div key={key}>
                      <h5 className="text-sm font-medium text-foreground mb-2 capitalize">
                        {key?.replace(/([A-Z])/g, ' $1')?.trim()}
                      </h5>
                      {Array.isArray(value) ? (
                        <ul className="space-y-1">
                          {value?.map((item, index) => (
                            <li key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                              <Icon name="ArrowRight" size={12} />
                              {item}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-muted-foreground">{value}</p>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="BookOpen"
                    iconSize={14}
                  >
                    Learn More
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Bell"
                    iconSize={14}
                  >
                    Set Alert
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Share"
                    iconSize={14}
                  >
                    Share
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* AI Model Info */}
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Info" size={16} className="text-primary" />
          <span className="text-sm font-medium text-foreground">AI Model Information</span>
        </div>
        <div className="text-xs text-muted-foreground space-y-1">
          <div>Model: TradePulse Neural Network v3.2</div>
          <div>Training Data: 5+ years of market data, 50+ indicators</div>
          <div>Last Updated: {new Date()?.toLocaleString()}</div>
          <div>Accuracy Rate: 78.3% over last 90 days</div>
        </div>
      </div>
    </div>
  );
};

export default AIInsightsPanel;